import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';

const STEP_KEYS = ['first', 'second', 'third'];

/**
 * "Siirry AutoFuturista": leaves a contact request. It mails Verstaani support only, and
 * we then reach out and hand over the AutoFutur export guide personally. Nothing about
 * exporting AutoFutur data is sent automatically to whoever signs up for a trial.
 */
function AutoFuturMigrationDialog({ open, onClose, onRequested, requestedAt, requestedEmail }) {
  const { t } = useTranslation();
  const { user, currentOrganization } = useUserStore();
  const [email, setEmail] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sentTo, setSentTo] = useState(null);

  useEffect(() => {
    if (open) {
      setEmail(requestedEmail || user?.email || '');
      setEditingEmail(false);
      setSending(false);
      setError(null);
      setSentTo(requestedAt ? requestedEmail || user?.email || '' : null);
    }
  }, [open, user, requestedAt, requestedEmail]);

  const handleSend = async () => {
    const orgId = currentOrganization?.organization_id;
    if (!orgId) return;
    setSending(true);
    setError(null);
    try {
      // Lead notification only: the request mails Verstaani support, nothing goes to the
      // customer. We hand out the AutoFutur export guide by hand after contacting them.
      const response = await axios.post(`/api/organizations/${orgId}/autofutur/request-migration/`, { email });
      setSentTo(response.data?.email || email);
      if (onRequested) onRequested(response.data);
    } catch (err) {
      if (err.response?.status === 409) {
        setSentTo(err.response.data?.email || email);
        if (onRequested) onRequested(err.response.data);
      } else {
        setError(t('onboardingChecklist.migration.error'));
      }
    } finally {
      setSending(false);
    }
  };

  const done = Boolean(sentTo);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="autofutur-migration-dialog">
      {done ? (
        <>
          <DialogContent sx={{ textAlign: 'center', py: 5 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'rgba(56, 161, 105, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <MarkEmailReadIcon sx={{ fontSize: 34, color: 'success.main' }} />
            </Box>
            <Typography sx={{ fontSize: '19px', fontWeight: 700 }}>
              {t('onboardingChecklist.migration.sentTitle')}
            </Typography>
            <Typography sx={{ fontSize: '14px', lineHeight: 1.6, color: 'text.secondary', mt: 1 }}>
              {t('onboardingChecklist.migration.sentBody', { email: sentTo })}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button fullWidth variant="outlined" onClick={onClose}>
              {t('onboardingChecklist.close')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            <Typography sx={{ fontSize: '17px', fontWeight: 700 }}>
              {t('onboardingChecklist.migration.title')}
            </Typography>
            <Typography sx={{ fontSize: '13.5px', lineHeight: 1.55, color: 'text.secondary', mt: 0.5 }}>
              {t('onboardingChecklist.migration.subtitle')}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, mb: 2.5 }}>
              {STEP_KEYS.map((key, index) => (
                <Box key={key} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      flexShrink: 0,
                      mt: '2px',
                      bgcolor: 'rgba(49, 130, 206, 0.1)',
                      color: 'secondary.main',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '13.5px', fontWeight: 700 }}>
                      {t(`onboardingChecklist.migration.steps.${key}.title`)}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', lineHeight: 1.5, color: 'text.secondary' }}>
                      {t(`onboardingChecklist.migration.steps.${key}.desc`)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                  }}
                >
                  {t('onboardingChecklist.migration.recipientLabel')}
                </Typography>
                {editingEmail ? (
                  <TextField
                    size="small"
                    fullWidth
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    sx={{ mt: 0.5 }}
                    inputProps={{ 'data-testid': 'autofutur-email-input' }}
                  />
                ) : (
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, wordBreak: 'break-all' }}>{email}</Typography>
                )}
              </Box>
              <Button
                size="small"
                variant="text"
                color="secondary"
                onClick={() => setEditingEmail((value) => !value)}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                {editingEmail ? t('onboardingChecklist.migration.emailDone') : t('onboardingChecklist.migration.changeEmail')}
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose} color="inherit">
              {t('onboardingChecklist.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SendIcon />}
              onClick={handleSend}
              disabled={sending || !email}
              data-testid="autofutur-send"
            >
              {t('onboardingChecklist.migration.send')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default AutoFuturMigrationDialog;
